<template>
  <div id="hls-video-player">
    <p>{{ cameraMsg }}</p>
    <video-player
      id="video-player"
      class="media-video-player card-video vjs-big-play-centered"
      ref="videoPlayer"
      :options="playerOptions"
      @ready="onPlayerReadied"
      @play="onPlayerPlay"
      @error="onPlayerError"
      @waiting="onWaiting"
      @playing="onPlaying"
    />
  </div>
</template>

<script>
// NOTE: 航海履歴からの移植を元に修正
import { videoPlayer } from "vue-videojs7";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import moment from "moment";
// Mixin
import { CameraMixin } from "@/mixins";
const MAX_PLAYBACK_VIDEO_FPS = 12;

export default {
  name: "HlsVideoPlayer",
  components: { videoPlayer },
  mixins: [CameraMixin],
  props: {
    isLive: { type: Boolean, default: false },

    current: { type: Number, required: false }, // timestamp
    start: { type: Number, required: false }, // timestamp
    end: { type: Number, required: false }, // timestamp
    fps: { type: Number, required: false }, // 倍速

    isPause: { type: Boolean, required: false },
    controls: { type: Boolean, default: false },
    autoPlay: { type: Boolean, default: false },
    hidePlayButton: { type: Boolean, default: false },

    width: { type: Number, required: false },

    camera: { type: Object, required: false },

    maxFps: {
      type: Number,
      default: MAX_PLAYBACK_VIDEO_FPS,
    },
  },

  data() {
    return {
      cameraStateOptions: Object.freeze({
        preparing: 1,
        playable: 2,
        playing: 3,
        failed: 4,
      }),
      cameraState: 0,

      cameraMsg: "",

      playerOptions: {
        fluid: this.width ? false : true,
        width: this.width,
        autoplay: this.autoPlay,
        muted: true,
        controls: this.controls,
        preload: "auto",
        fullscreen: false,
        bigPlayButton: false,
        flash: { vls: { withCredentials: false } },
        html5: {
          vls: {
            withCredentials: false,
            liveRangeSafeTimeDelta: 20000,
          },
        },
        language: "ja",
        languages: {
          ja: {
            Play: "再生",
            "The media could not be loaded, either because the server or network failed or because the format is not supported.":
              "ネットワークに障害が発生したか、カメラが停止したため、再生できません",
          },
        },
        controlBar: { pictureInPictureToggle: false },
      },
      initialized: false,

      hasVideoError: false,

      isAttemptedDirectConnect: false,
    };
  },
  watch: {
    current(value, oldValue) {
      if (this.isPause) {
        this.player.currentTime(value - this.start);
      } else if (
        oldValue &&
        Math.abs(this.player.currentTime() - (value - this.start)) < this.fps
      ) {
        return;
      }
      this.player.currentTime(value - this.start);
    },
    fps(value) {
      if (this.player) {
        if (value > this.maxFps) return;
        this.player.playbackRate(value);
      }
    },
    isPause(value) {
      if (!this.player) return;
      if (value) {
        this.player.pause();
      } else {
        this.player.play();
      }
    },

    cameraState(value) {
      const { preparing, playable, playing, failed } = this.cameraStateOptions;
      switch (value) {
        case preparing:
          this.hasVideoError = false;
          this.player.error(null);
          if (!this.hidePlayButton) {
            this.hideVideoPlayButton();
          }
          this.cameraMsg = "接続中...";
          break;
        case playable:
          this.hasVideoError = false;
          this.player.error(null);
          if (!this.hidePlayButton) {
            this.showVideoPlayButton();
          }
          this.cameraMsg = "";
          break;
        case playing:
          this.hasVideoError = false;
          this.player.error(null);
          if (!this.hidePlayButton) {
            this.hideVideoPlayButton();
          }
          this.cameraMsg = "";
          break;
        case failed:
          this.hasVideoError = true;
          if (!this.hidePlayButton) {
            this.hideVideoPlayButton();
          }
          this.cameraMsg = "";
          this.player.error("映像が見つかりません");
          break;
      }
    },
  },
  computed: {
    player() {
      if (typeof this.$refs.videoPlayer === "undefined") return null;
      return this.$refs.videoPlayer.player;
    },
  },
  async created() {
    if (this.isLive) this.playerOptions.playbackRates = [1];
  },
  async mounted() {
    this.cameraState = this.cameraStateOptions.preparing;
    this.attemptToConnect();
  },
  methods: {
    calculatePlayBackTimeNowByPlayer() {
      return this.player.currentTime() + this.start;
    },
    async onPlayerReadied(_player) {
      if (!this.initialized) {
        this.initialized = true;
        this.player.currentTime(this.current - this.start);
        this.player.playbackRate(this.fps);
      }
    },

    onPlayerPlay(_player) {
      this.cameraState = this.cameraStateOptions.playing;
      this.player.playbackRate(this.fps);
    },

    onPlayerError(_e) {
      if (!this.isAttemptedDirectConnect) {
        this.isAttemptedDirectConnect = true;
        this.directConnect();
      }
    },

    onWaiting() {
      this.safeBufferPause();
    },

    onPlaying() {
      this.safeBufferPause();
    },

    hideVideoPlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      if (playButton.length !== 0) {
        playButton[0].style.display = "none";
      }
    },

    showVideoPlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      if (playButton.length !== 0) {
        playButton[0].style.display = "block";
      }
    },

    async attemptToConnect() {
      await this.directConnect();
    },

    safeBufferPause() {
      let bufferEnd = this.player.buffered().end(0);
      let current = this.player.currentTime();
      // 8倍速以上で、かつバッファが一定秒未満のとき再生を止める
      if (
        this.player.playbackRate() >= 8 &&
        current + this.safeBufferThreshold > bufferEnd // TODO: コピー元に定義されていたが、既存で条件が必ずfalseになる
      ) {
        this.player.pause();
        this.$vs.notify({
          title:
            "動画バッファ不足：再生速度を下げるか、しばらくしてから試してください",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
    },

    async directConnect() {
      let url = await this.connectToCamera();
      if (url.error_code) {
        this.cameraState = this.cameraStateOptions.failed;
      } else {
        this.playDirectVideo(url);
      }
    },

    async connectToCamera() {
      const payload = {
        started_at: moment.unix(this.start).utc().toISOString(),
        ended_at: moment.unix(this.end).utc().toISOString(),
      };
      const res = await AiseaApiFactory.get("enterpriseCamera").onDemandByHash(
        this.camera.hash,
        payload,
      );

      return res.data;
    },

    playDirectVideo(m3u8String) {
      const blob = new Blob([m3u8String], { type: "application/x-mpegurl" });
      const blobUrl = URL.createObjectURL(blob);
      if (this.player) {
        const video = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: blobUrl,
        };
        this.player.src(video);
        this.cameraState = this.cameraStateOptions.playable;
      }
    },
  },
  beforeDestroy() {
    if (this.$refs.videoPlayer) {
      this.$refs.videoPlayer.$destroy();
    }
  },
};
</script>

<style lang="scss">
#video-player {
  .video-js .vjs-time-control {
    display: block;
  }
  .video-js .vjs-remaining-time {
    display: none;
  }
}
</style>
<style lang="scss" scoped>
#hls-video-player {
  position: relative;
  .no-video {
    background-color: #000000;
  }
  button {
    z-index: 5;
    position: absolute;
    top: 65%;
    left: 50%;
    transform: translateX(-50%);
    width: fit-content;
  }
  p {
    position: absolute;
    top: 50%;
    left: 50%;
    -ms-transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 0;
    color: white;
    z-index: 5;
  }
}
</style>
